import React, { useState } from "react";
import { toast } from "sonner";
import { Languages, SpellCheck2, Sparkles, Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import CoverLetterDialog from "@/components/resume-builder/CoverLetterDialog";
import ShareResumeCard from "@/components/resume-builder/ShareResumeCard";
import QuantifyAchievementsCard from "@/components/resume-builder/QuantifyAchievementsCard";
import TrimToFitCard from "@/components/resume-builder/TrimToFitCard";
import ResumeAnalyticsCard from "@/components/resume-builder/ResumeAnalyticsCard";
import PracticeInterviewCard from "@/components/resume-builder/PracticeInterviewCard";

/** "What would you like to do?" -- AI Tools hub (mirrors FlowCV's AI Tools panel), used both
 * as Wizard Step 4 (inline) and inside the always-available floating AI Tools dialog in
 * Advanced mode. Never mounted in both places at once (see ResumeBuilderPage.js). */
export default function AiToolsPanel({ draft, setDraft, setActiveVersionId }) {
  const [translating, setTranslating] = useState(false);
  const [checkingGrammar, setCheckingGrammar] = useState(false);
  const [grammarResult, setGrammarResult] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const translateToEnglish = async () => {
    setTranslating(true);
    try {
      const { data } = await api.post("/resume-builder/translate", { draft });
      setDraft(data.draft);
      setActiveVersionId(data.id);
      toast.success(`Created "${data.name}" -- an English copy saved to Version History`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not translate your resume right now");
    } finally {
      setTranslating(false);
    }
  };

  const checkGrammar = async () => {
    setCheckingGrammar(true);
    try {
      const { data } = await api.post("/resume-builder/check-grammar", { draft });
      setGrammarResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check spelling & grammar right now");
    } finally {
      setCheckingGrammar(false);
    }
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const { data } = await api.post("/resume-builder/generate-summary", {
        target_role: draft.target_role, skills: draft.skills,
        experience_titles: draft.experience.map((e) => e.title).filter(Boolean),
      });
      setDraft({ ...draft, summary: data.summary });
      toast.success("Summary generated -- check the Basics step");
    } catch (e) {
      toast.error("Could not generate summary right now");
    } finally {
      setGeneratingSummary(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="resume-ai-tools-panel">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">AI Tools</span>
        <h3 className="font-heading text-xl font-semibold text-white mt-1">What would you like to do?</h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="glass-panel rounded-2xl p-4" data-testid="ai-tool-card-translate">
          <div className="flex items-center gap-2 mb-1.5">
            <Languages className="w-4 h-4 text-sky-400" />
            <p className="text-sm font-semibold text-white">Translate Resume</p>
          </div>
          <p className="text-xs text-slate-400 mb-3">Instantly create a translated (English) copy of your resume, saved as a new version.</p>
          <button
            onClick={translateToEnglish} disabled={translating} data-testid="ai-tool-translate-button"
            className="flex items-center justify-center gap-1.5 w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />} Translate to English
          </button>
        </div>

        <div className="glass-panel rounded-2xl p-4" data-testid="ai-tool-card-grammar">
          <div className="flex items-center gap-2 mb-1.5">
            <SpellCheck2 className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-white">Check Spelling &amp; Grammar</p>
          </div>
          <p className="text-xs text-slate-400 mb-3">Scan your resume for spelling and grammar issues, with suggestions on how to fix each one.</p>
          <button
            onClick={checkGrammar} disabled={checkingGrammar} data-testid="ai-tool-grammar-button"
            className="flex items-center justify-center gap-1.5 w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {checkingGrammar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SpellCheck2 className="w-3.5 h-3.5" />} Check Now
          </button>
          {grammarResult && (
            <div className="mt-3 space-y-2" data-testid="ai-tool-grammar-results">
              {grammarResult.issues.length === 0 ? (
                <p className="text-xs text-emerald-300 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {grammarResult.overall_note}</p>
              ) : (
                <>
                  {grammarResult.issues.map((issue, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-2.5" data-testid={`ai-tool-grammar-issue-${i}`}>
                      <p className="text-xs text-rose-300 line-through">{issue.original_text}</p>
                      <p className="text-xs text-emerald-300 mt-0.5">{issue.corrected_text}</p>
                      <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" /> {issue.explanation}</p>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400 italic">{grammarResult.overall_note}</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="glass-panel rounded-2xl p-4" data-testid="ai-tool-card-summary">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <p className="text-sm font-semibold text-white">Generate Summary</p>
          </div>
          <p className="text-xs text-slate-400 mb-3">Generate a tailored professional summary from your skills and experience.</p>
          <button
            onClick={generateSummary} disabled={generatingSummary} data-testid="ai-tool-generate-summary-button"
            className="flex items-center justify-center gap-1.5 w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {generatingSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate Now
          </button>
        </div>

        <div className="glass-panel rounded-2xl p-4" data-testid="ai-tool-card-cover-letter">
          <div className="flex items-center gap-2 mb-1.5">
            <Mail className="w-4 h-4 text-rose-400" />
            <p className="text-sm font-semibold text-white">Draft Cover Letter</p>
          </div>
          <p className="text-xs text-slate-400 mb-3">Create a tailored cover letter from your resume and target role/company.</p>
          <CoverLetterDialog draft={draft} />
        </div>

        <QuantifyAchievementsCard draft={draft} setDraft={setDraft} />
        <TrimToFitCard draft={draft} setDraft={setDraft} />
        <PracticeInterviewCard />
        <ShareResumeCard />
        <ResumeAnalyticsCard />
      </div>
    </div>
  );
}
